/**
 * Address formatting utilities
 * Following patterns from main B18 app processAddressNew and getStopsAddress
 */

/**
 * Format city and state
 * @param city - City name
 * @param state - State name or abbreviation
 * @returns Formatted "City, State" string
 */
export const formatCityState = (city?: string, state?: string): string => {
  const parts = [city, state].filter(Boolean)
  return parts.join(', ')
}

/**
 * Format full address like main app SelectAsync facilityList
 * Pattern: "Address, City, State, Zip"
 */
export const formatFullAddress = (
  addressDetails?: {
    address?: string
    city?: string
    state?: string
    zipCode?: string
    zip?: string
    street?: string
  } | null
): string => {
  if (!addressDetails) return ''

  const parts = [
    addressDetails.address || addressDetails.street,
    addressDetails.city,
    addressDetails.state,
    addressDetails.zipCode || addressDetails.zip
  ].filter(Boolean)

  return parts.join(', ')
}

/**
 * Resolved address parts from a stop object
 * Used internally to ensure consistent address resolution across all functions
 */
interface ResolvedAddressParts {
  street: string
  city: string
  state: string
  zipCode: string
}

/**
 * Resolve address parts from a stop object
 * Pattern from main app: merges facilityDetails.addressDetails with cityStateAddressDetails
 *
 * This is the single source of truth for address resolution - all address
 * formatting functions should use this to ensure consistency (DRY principle)
 *
 * @param stop - Stop object with facilityDetails and/or cityStateAddressDetails
 * @returns Resolved address parts from all available sources
 */
const resolveStopAddressParts = (stop: any): ResolvedAddressParts => {
  if (!stop) {
    return { street: '', city: '', state: '', zipCode: '' }
  }

  const facilityAddr = stop.facilityDetails?.addressDetails
  const cityStateAddr = stop.cityStateAddressDetails

  return {
    // Street address - prefer facilityDetails
    street: facilityAddr?.address || facilityAddr?.street || '',
    // City - try facilityDetails first, fall back to cityStateAddressDetails, then stop-level
    city: facilityAddr?.city || cityStateAddr?.city || stop.city || '',
    // State - same fallback pattern
    state: facilityAddr?.state || cityStateAddr?.state || stop.state || '',
    // Zip code - same fallback pattern
    zipCode:
      facilityAddr?.zipCode ||
      facilityAddr?.zip ||
      cityStateAddr?.zipCode ||
      cityStateAddr?.zip ||
      ''
  }
}

/**
 * Format stop address - show full address with city/state
 * Pattern from main app: processAddressNew and getStopsAddress
 *
 * @param stop - Stop object with facilityDetails and/or cityStateAddressDetails
 * @returns Full formatted address string
 * @example
 * formatStopAddress(stop) // "2630 Saint Matthews Road, Orangeburg, SC, 29115"
 */
export const formatStopAddress = (stop: any): string => {
  if (!stop) return ''
  // Handle hidden address - show city/state only from cityStateAddressDetails
  if (stop.hideAddress) {
    if (stop.cityStateAddressDetails) {
      return formatCityState(
        stop.cityStateAddressDetails.city,
        stop.cityStateAddressDetails.state
      )
    }
    return ''
  }

  // Use common resolution logic
  const { street, city, state, zipCode } = resolveStopAddressParts(stop)

  // Build full address with all available parts
  const parts = [street, city, state, zipCode].filter(Boolean)

  if (parts.length > 0) {
    return parts.join(', ')
  }

  // Final fallback - check for formattedAddress in cityStateAddressDetails
  if (stop.cityStateAddressDetails?.formattedAddress) {
    return stop.cityStateAddressDetails.formattedAddress
  }

  return ''
}

/**
 * Get facility name
 * Pattern from main app: SelectAsync shows facility name
 */
export const getFacilityName = (stop: any): string => {
  if (!stop) return ''

  // For hidden address, show displayName if available
  if (stop.hideAddress && stop.cityStateAddressDetails?.displayName) {
    return stop.cityStateAddressDetails.displayName
  }

  // Return full facility name
  return stop.facilityDetails?.name || stop.facilityName || ''
}

/**
 * Get facility display like SelectAsync
 * Pattern: "FacilityName - Address, City, State, Zip"
 */
export const getFacilityDisplay = (
  stop: any
): { name: string; address: string } => {
  const name = getFacilityName(stop)
  const address = formatStopAddress(stop)
  return { name, address }
}
